import * as React from 'react';
import styled from 'styled-components';
import { IoChevronDown } from 'react-icons/io5';
import ReactTooltip from 'react-tooltip';
import { CardButton, TextualCardLayout } from '@/components/card-button';
import { useIsPreviewingDesktop } from '@/features/editor/shared/use-device-type';

export interface TargetingButtonProps {
  title: string;
  subtitle: string;
  onClick?: () => void;
  isDisabled?: boolean;
  wrapperRef?: React.MutableRefObject<HTMLElement>;
}

export function TargetingButton({
  title,
  subtitle,
  onClick,
  wrapperRef,
  isDisabled,
}: TargetingButtonProps) {
  const isDesktop = useIsPreviewingDesktop();
  const subtitleRef = React.useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);
  const tooltipId = React.useId();

  React.useEffect(() => {
    const checkTruncation = () => {
      const element = subtitleRef.current;
      if (element) {
        // Check if text is truncated by comparing scroll width with client width
        setIsTruncated(element.scrollWidth > element.clientWidth);
      }
    };

    checkTruncation();

    // Re-check truncation on window resize
    window.addEventListener(`resize`, checkTruncation);
    return () => {
      window.removeEventListener(`resize`, checkTruncation);
    };
  }, [subtitle, isDesktop]);

  const onClicked = () => {
    onClick && onClick();
  };

  return (
    <>
      <CardButton
        wrapperRef={wrapperRef}
        canBeSelected
        onClicked={onClicked}
        padding="0rem 1rem 0rem 0rem"
        borderRadius="1.2rem"
        isDisabled={isDisabled}
      >
        <StyledTextualCardLayout
          verticalGap="0.3rem"
          padding="0.7rem 2rem"
          accentFontFamily="JetBrains Mono"
        >
          <Title isDesktop={isDesktop}>{title}</Title>
          <Subtitle
            ref={subtitleRef}
            isDesktop={isDesktop}
            data-tip={isTruncated ? subtitle : undefined}
            data-for={isTruncated ? tooltipId : undefined}
          >
            {subtitle}
          </Subtitle>
        </StyledTextualCardLayout>
        <Arrow>
          <IoChevronDown size={13} />
        </Arrow>
      </CardButton>
      {isTruncated && (
        <ReactTooltip
          id={tooltipId}
          place="bottom"
          effect="solid"
          backgroundColor="#fff"
          textColor="#000"
          className="targeting-button-tooltip"
        >
          {subtitle}
        </ReactTooltip>
      )}
    </>
  );
}

const StyledTextualCardLayout = styled(TextualCardLayout)``;

const Title = styled.span`
  font-size: 1rem !important;
  font-weight: bold !important;
  color: #777e81 !important;
  letter-spacing: -0.42px !important;
  text-transform: capitalize !important;
  font-family: 'JetBrains Mono', serif !important;
`;

const Subtitle = styled.span`
  font-size: ${(p: { isDesktop: boolean }) =>
    p.isDesktop ? `1.1rem` : `1.3rem`} !important;
  font-weight: bolder !important;
  color: black !important;
  letter-spacing: -0.42px !important;
  text-transform: uppercase !important;
  max-width: ${(p: { isDesktop: boolean }) =>
    p.isDesktop ? `8rem` : `14rem`} !important;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const Arrow = styled.div`
  position: absolute;
  top: 0;
  right: 0.75rem;
  bottom: 0;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  color: black;
`;
